import CryptoJS from 'crypto-js'

const key = getFirst32(import.meta.env.VITE_APP_ID || '')

import { useRandomArrayStore } from '@/stores/useRandomArrayStore'

type MethodMap = Record<string, string>

interface FlutterMessage {
  type: string
  data?: any
}

interface BridgeParams {
  type: string
  data?: any
}

/**
 * 随机字符串（用于混淆）
 */
function getRandomLetters(len = 3) {
  const letters = 'abcdefghijklmnopqrstuvwxyz'
  let result = ''

  for (let i = 0; i < len; i++) {
    result += letters[Math.floor(Math.random() * letters.length)]
  }

  return result
}

function getFirst32(str: string) {
  if (str.length >= 32) {
    return str.substring(0, 32)
  }

  return str.padEnd(32, '0')
}

class Bridge {
  private methodMap: MethodMap = {}
  private ready = false

  /**
   * 初始化
   */
  init() {
    console.log('[Bridge] init')

    // JSON 通道备用
    window.onFlutterMessage = (message: any) => {
      try {
        const result = typeof message === 'string' ? JSON.parse(message) : message

        this.handleMessage(result)
      } catch (e) {
        console.error('Flutter消息解析失败', e)
      }
    }

    // 🚀 挂载 Flutter → H5 方法
    this.mountFlutterMethods()
    const randomStore = useRandomArrayStore()

    // 🚀 生成能力表（给 Flutter 用）
    const rawMethods = randomStore.randomArray

    const data = rawMethods.map(name => {
      return `${getRandomLetters()}${name}`
    })

    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key).toString()

    // 通知 Flutter 初始化
    this.call('cherryInitBridge', encrypted)
  }

  /**
   * 🚀 核心：Flutter → H5 方法挂载
   * Flutter 调用：window.paymentResult(...)
   */
  private mountFlutterMethods() {
    const randomStore = useRandomArrayStore()
    const methods = randomStore.randomArray

    methods.forEach(name => {
      ;(window as any)[name] = (payload: any) => {
        this.handleFlutterCall(name, payload)
      }
    })
  }

  /**
   * 🚀 Flutter → H5 统一入口
   */
  private handleFlutterCall(type: string, payload: any) {
    console.log(`[Flutter -> H5] ${type}`, payload)

    let data = payload

    // 尝试解密（如果是 AES）
    try {
      const bytes = CryptoJS.AES.decrypt(payload, key)
      const decrypted = bytes.toString(CryptoJS.enc.Utf8)

      if (decrypted) {
        data = JSON.parse(decrypted)
      }
    } catch (e) {
      // 非加密数据忽略
    }

    /**
     * 🚀 核心：同时支持 2 种模式
     * 1. window.xxx（函数调用模式）
     * 2. addEventListener（事件模式）
     */

    const handler = (window as any)[type]

    // ① 如果是函数模式（直接调用）
    if (typeof handler === 'function') {
      handler(data)
    }

    // ② 同时触发事件模式（Vue 推荐用这个）
    window.dispatchEvent(
      new CustomEvent(type, {
        detail: data
      })
    )
  }

  /**
   * H5 → Flutter
   */
  async call(type: string, data: any = {}) {
    await this.waitReady()

    const realType = this.methodMap[type] || type

    const params: BridgeParams = {
      type: realType,
      data
    }

    const message = JSON.stringify(params)

    window.cherryFlutterBridge?.postMessage?.(message)

    console.log('[H5 -> Flutter]', params)
  }

  /**
   * 等待 Flutter 注入
   */
  waitReady(): Promise<void> {
    return new Promise(resolve => {
      if (this.ready) return resolve()

      const timer = setInterval(() => {
        if (window.cherryFlutterBridge?.postMessage) {
          clearInterval(timer)
          this.ready = true
          resolve()
        }
      }, 50)
    })
  }

  /**
   * JSON 通道备用
   */
  private handleMessage(message: FlutterMessage) {
    switch (message.type) {
      case 'bridgeInit':
        this.methodMap = message.data || {}
        this.ready = true
        console.log('[Bridge映射表]', this.methodMap)
        break

      case 'appResume':
        window.dispatchEvent(new CustomEvent('appResume', { detail: message.data }))
        break

      case 'appPause':
        window.dispatchEvent(new CustomEvent('appPause', { detail: message.data }))
        break

      case 'networkChange':
        window.dispatchEvent(new CustomEvent('networkChange', { detail: message.data }))
        break

      case 'loginSuccess':
        window.dispatchEvent(new CustomEvent('loginSuccess', { detail: message.data }))
        break

      default:
        console.log('[未知Flutter消息]', message)
    }
  }

  /**
   * 手动设置映射
   */
  setMethodMap(map: MethodMap) {
    this.methodMap = map
    this.ready = true
  }
}

export default new Bridge()
